import { Component, inject } from '@angular/core';
import { WorkflowBuilderService } from "../services/workflow-builder.service";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { AdminControllerComponent } from "../../../../../shared/admin-controller/admin-controller.component";

/**
 * WorkflowAdminComponent manages the admin step of the workflow builder.
 * It handles the form for workflow admin settings (such as isEnabled and isUniversal)
 * and synchronizes these settings with the workflow service.
 */
@Component({
  selector: 'workflow-admin',
  imports: [
    AdminControllerComponent
  ],
  templateUrl: './workflow-admin.component.html',
  styleUrl: './workflow-admin.component.scss'
})
export class WorkflowAdminComponent {
  adminForm!: FormGroup;

  #formBuilder = inject(FormBuilder);
  #workflowBuilderService = inject(WorkflowBuilderService);

  constructor() {
    this.adminForm = this.#formBuilder.group({
      isEnabled: new FormControl(this.#workflowBuilderService.workflow().details?.isEnabled ?? true),
      isUniversal: new FormControl(this.#workflowBuilderService.workflow().details?.isUniversal ?? false),
    })
  }

  /**
   * Resets the admin form to its initial state, clearing all values and validation states.
   */
  resetForm() {
    this.adminForm.reset();
    this.adminForm.markAsPristine();
    this.adminForm.markAsUntouched();
  }

  /**
   * Updates the workflow service with the current values from the admin form.
   * Synchronizes admin settings (isEnabled, isUniversal) with the workflow state.
   */
  updateWorkflow() {
    const formValues = this.adminForm.value;
    this.#workflowBuilderService.workflow.update(current => ({
      ...current,
      details: {
        ...current.details,
        ...formValues
      }
    }));
  }

  /**
   * Marks all controls in the workflow form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the WorkflowBuilderComponent.
   */
  markFormAsTouched(): void {
    this.adminForm.markAllAsTouched();
  }

  isValid(): boolean {
    const isEnabled = this.adminForm.get('isEnabled')?.value;
    const isUniversal = this.adminForm.get('isUniversal')?.value;
    return this.adminForm.valid && typeof isEnabled === 'boolean' && typeof isUniversal === 'boolean';
  }
}
